import { useEffect, useState } from 'react';
import { format, isValid } from 'date-fns';

/**
 * echarts theming tokens for every chart in the app (user statistics, per-post engagement, …).
 *
 * The app is dark-by-default with no user-facing toggle yet (ThemeProvider.tsx), but charts read
 * these tokens through `useIsDarkMode` (which watches the `.dark` class via MutationObserver)
 * so they stay correct if/when a toggle lands, rather than hardcoding one mode.
 *
 * Values are the dataviz-skill reference palette (categorical slots 1-6 + chart-chrome ink),
 * validated for CVD-safety + contrast against this app's actual light/dark surfaces — see the
 * dataviz palette reference for the full slot table.
 */

export interface ThemeTone {
  light: string;
  dark: string;
}

/** Tracks the `.dark` class on <html> so chart colors can react if a light/dark toggle is added. */
export function useIsDarkMode(): boolean {
  const [isDark, setIsDark] = useState(() => document.documentElement.classList.contains('dark'));
  useEffect(() => {
    const el = document.documentElement;
    const observer = new MutationObserver(() => setIsDark(el.classList.contains('dark')));
    observer.observe(el, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);
  return isDark;
}

export function tone(t: ThemeTone, isDark: boolean): string {
  return isDark ? t.dark : t.light;
}

/** Chart chrome (ink / grid / surface) — dataviz palette "Chart chrome & ink" table. */
export const CHART_INK = {
  primary: { light: '#0b0b0b', dark: '#ffffff' },
  secondary: { light: '#52514e', dark: '#c3c2b7' },
  muted: { light: '#898781', dark: '#b0b0b0' }, // dark matches --muted-foreground / Text.secondary
  grid: { light: '#e1e0d9', dark: '#2c2c2a' },
  axis: { light: '#c3c2b7', dark: '#383835' },
  surface: { light: '#fcfcfb', dark: '#1a1a1a' },
} satisfies Record<string, ThemeTone>;

/**
 * Engagement chart series — fixed categorical order (slots 1-4: blue/aqua/yellow/green), matching
 * CMP's Purchases/Likes/Comments/Tips order (StatisticsServiceImpl.ENGAGEMENT_HEADERS). Validated:
 * light worst-adjacent CVD dE 24.2 (PASS), dark worst-adjacent dE 10.3 (floor band — relief via
 * direct labels + legend).
 */
export const ENGAGEMENT_SERIES_COLORS: Record<string, ThemeTone> = {
  Purchases: { light: '#2a78d6', dark: '#3987e5' },
  Likes: { light: '#1baf7a', dark: '#199e70' },
  Comments: { light: '#eda100', dark: '#c98500' },
  Tips: { light: '#008300', dark: '#008300' },
};

/**
 * Earnings categories — fixed categorical order (slots 1-6), matching
 * StatisticsServiceImpl.OVERVIEW_HEADERS (SUBSCRIBE/TIP/EXCLUSIVE/REFERRAL/MESSAGE/LIVESTREAM).
 */
export const EARNINGS_CATEGORY_COLORS: Record<string, ThemeTone> = {
  SUBSCRIBE: { light: '#2a78d6', dark: '#3987e5' },
  TIP: { light: '#1baf7a', dark: '#199e70' },
  EXCLUSIVE: { light: '#eda100', dark: '#c98500' },
  REFERRAL: { light: '#008300', dark: '#008300' },
  MESSAGE: { light: '#4a3aa7', dark: '#9085e9' },
  LIVESTREAM: { light: '#e34948', dark: '#e66767' },
};

/** Muted fallback for any header name outside the fixed sets above (defensive — never a fabricated hue). */
export const FALLBACK_SERIES_COLOR: ThemeTone = { light: '#7a7a78', dark: '#9a9a97' };

/** `MMM d` axis label from an ISO date-time string (falls back to the raw string if unparsable). */
export function formatAxisDate(iso: string): string {
  const d = new Date(iso);
  return isValid(d) ? format(d, 'MMM d') : iso;
}
